import { useState, useEffect } from 'react';
import { clientesAPI, Cliente } from '../services/clientesAPI';
import { useToast } from './useToast';

interface UseClientesReturn {
  clientes: Cliente[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createCliente: (data: any) => Promise<Cliente | null>;
  updateCliente: (id: number, data: any) => Promise<Cliente | null>;
  deleteCliente: (id: number) => Promise<boolean>;
}

interface UseClientesParams {
  search?: string;
  tipo_cliente?: 'persona_natural' | 'juridica';
  activo?: boolean;
  limit?: number;
  page?: number;
}

export const useClientes = (
  // `enabled: false` evita la consulta por completo. Existe porque
  // `GET /clientes` exige el permiso `clientes.ver`, que sólo tiene el
  // administrador: una pantalla que muestre datos de clientes sólo a admin no
  // debe pedirlos con los demás roles, o se lleva un 403 y un toast de error
  // por algo que ni siquiera va a usar.
  params?: UseClientesParams & { enabled?: boolean }
): UseClientesReturn => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { error: toastError, success: toastSuccess } = useToast();

  const habilitado = params?.enabled !== false;

  const fetchClientes = async () => {
    if (!habilitado) {
      setClientes([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await clientesAPI.listar(params);

      if (response.success) {
        setClientes(response.data);
      } else {
        throw new Error('Error al cargar clientes');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      toastError('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const createCliente = async (data: any): Promise<Cliente | null> => {
    try {
      const response = await clientesAPI.crear(data);

      if (response.success) {
        setClientes((prev) => [...prev, response.data]);
        toastSuccess('Éxito', response.message);
        return response.data;
      } else {
        throw new Error('Error al crear cliente');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error al crear cliente';
      toastError('Error', errorMessage);
      return null;
    }
  };

  const updateCliente = async (
    id: number,
    data: any
  ): Promise<Cliente | null> => {
    try {
      const response = await clientesAPI.actualizar(id, data);

      if (response.success) {
        setClientes((prev) =>
          prev.map((cliente) => (cliente.id === id ? response.data : cliente))
        );
        toastSuccess('Éxito', response.message);
        return response.data;
      } else {
        throw new Error('Error al actualizar cliente');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error al actualizar cliente';
      toastError('Error', errorMessage);
      return null;
    }
  };

  const deleteCliente = async (id: number): Promise<boolean> => {
    try {
      const response = await clientesAPI.eliminar(id);

      if (response.success) {
        setClientes((prev) =>
          prev.map((cliente) =>
            cliente.id === id ? { ...cliente, activo: false } : cliente
          )
        );
        toastSuccess('Éxito', response.message);
        return true;
      } else {
        throw new Error('Error al eliminar cliente');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error al eliminar cliente';
      toastError('Error', errorMessage);
      return false;
    }
  };

  const refetch = async () => {
    await fetchClientes();
  };

  useEffect(() => {
    fetchClientes();
  }, [
    params?.search,
    params?.tipo_cliente,
    params?.activo,
    params?.limit,
    params?.page,
    habilitado,
  ]);

  return {
    clientes,
    loading,
    error,
    refetch,
    createCliente,
    updateCliente,
    deleteCliente,
  };
};
